/**
 * Generic useSchemaMigration Hook — Feature-agnostic schema version check.
 *
 * sl-oll9.10: Extracted from useElsterSchemaMigration. Features provide:
 * - REST endpoints for schema fetching
 * - mapSchemaToColumns for feature-specific column definitions
 * - extractFieldId for matching existing columns to schema fields
 *
 * SOLID:
 * - S: Only migration state management
 * - O: New features = new config, no code changes here
 * - D: Depends on schema-diff.ts, not feature-specific implementations
 */

import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { type DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import { useEffect, useMemo, useState } from 'react';

import {
  type ColumnDefinition,
  computeSchemaDiff,
  type ExistingColumn,
  type SchemaDiff,
} from './schema-diff';

// ─── Config ──────────────────────────────────────────────────────────────────

export interface SchemaMigrationConfig<TSchema> {
  /** Current workspace ID */
  workspaceId: string;
  /** REST endpoint for fetching available schema versions (returns { versions: string[] }) */
  versionsEndpoint: string;
  /** REST endpoint for fetching a schema by year (receives ?year= query param) */
  schemaEndpoint: string;
  /** Optional REST endpoint for can-update guard (returns { canUpdate, blockingRows }) */
  canUpdateEndpoint?: string;
  /** DatabaseBlockDataSource for the target database */
  dataSource: DatabaseBlockDataSource | null;
  /** Current schema version stored on the block (null if never migrated) */
  currentVersion: string | null;
  /** Convert the fetched schema to generic column definitions */
  mapSchemaToColumns: (schema: TSchema) => ColumnDefinition[];
  /** Extract logical field ID from an existing column name (for matching) */
  extractFieldId: (columnName: string) => string | null;
  /** Build the target version string from the schema */
  getVersionString: (schema: TSchema) => string;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface MigrationState<TSchema = unknown> {
  /** Whether a schema migration is needed */
  needsMigration: boolean;
  /** The computed diff (null if no migration or still loading) */
  diff: SchemaDiff | null;
  /** The fetched schema (needed for migration execution) */
  schema: TSchema | null;
  /** Whether the user dismissed the dialog (show banner instead) */
  dismissed: boolean;
  /** Whether schema check is still loading */
  loading: boolean;
  /** Number of blocking rows (0 = safe to update) */
  blockingRows: number;
  /** Dismiss the migration dialog (switch to banner mode) */
  dismiss: () => void;
  /** Mark migration as complete (after execution) */
  markComplete: () => void;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSchemaMigration<TSchema>(
  config: SchemaMigrationConfig<TSchema>
): MigrationState<TSchema> {
  const [schema, setSchema] = useState<TSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [blockingRows, setBlockingRows] = useState(0);

  const {
    workspaceId,
    versionsEndpoint,
    schemaEndpoint,
    canUpdateEndpoint,
    dataSource,
    currentVersion,
    mapSchemaToColumns,
    extractFieldId,
    getVersionString,
  } = config;

  // Fetch latest schema from backend
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        // 1. Get available schema years
        const versionsRes = await workspaceApiFetch(
          versionsEndpoint,
          { credentials: 'include' },
          {
            workspaceId,
            category: 'metadata',
            mode: 'background',
            priority: 'visible',
          }
        );
        if (!versionsRes.ok || cancelled) {
          setLoading(false);
          return;
        }
        const { versions } = (await versionsRes.json()) as {
          versions: string[];
        };
        if (versions.length === 0 || cancelled) {
          setLoading(false);
          return;
        }

        // 2. Use the latest year
        const latestYear = versions[versions.length - 1];

        // 3. Fetch the schema for that year
        const schemaRes = await workspaceApiFetch(
          `${schemaEndpoint}?year=${latestYear}`,
          { credentials: 'include' },
          {
            workspaceId,
            category: 'metadata',
            mode: 'background',
            priority: 'visible',
          }
        );
        if (!schemaRes.ok || cancelled) {
          setLoading(false);
          return;
        }
        const data = (await schemaRes.json()) as TSchema;
        if (!cancelled) {
          setSchema(data);
        }

        // 4. Optional: check can-update guard
        if (canUpdateEndpoint) {
          const guardRes = await workspaceApiFetch(
            canUpdateEndpoint,
            { credentials: 'include' },
            {
              workspaceId,
              category: 'metadata',
              mode: 'background',
              priority: 'visible',
            }
          );
          if (guardRes.ok && !cancelled) {
            const guard = (await guardRes.json()) as {
              canUpdate: boolean;
              blockingRows: number;
            };
            setBlockingRows(guard.blockingRows);
          }
        }

        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    run().catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, versionsEndpoint, schemaEndpoint, canUpdateEndpoint]);

  // Read existing columns from database
  const existingColumns: ExistingColumn[] = useMemo(() => {
    if (!dataSource) return [];

    const propIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;
    return propIds.map((propId: string) => ({
      id: propId,
      name: dataSource.propertyNameGet(propId),
      type: dataSource.propertyTypeGet(propId) ?? 'unknown',
    }));
  }, [dataSource]);

  // Compute diff
  const diff = useMemo(() => {
    if (!schema || existingColumns.length === 0) return null;
    const targetColumns = mapSchemaToColumns(schema);
    const targetVersion = getVersionString(schema);
    return computeSchemaDiff(
      targetColumns,
      existingColumns,
      currentVersion,
      targetVersion,
      extractFieldId
    );
  }, [
    schema,
    existingColumns,
    currentVersion,
    mapSchemaToColumns,
    getVersionString,
    extractFieldId,
  ]);

  const needsMigration = diff !== null && !diff.isEmpty && !completed;

  return {
    needsMigration,
    diff: needsMigration ? diff : null,
    schema: needsMigration ? schema : null,
    dismissed,
    loading,
    blockingRows,
    dismiss: () => setDismissed(true),
    markComplete: () => setCompleted(true),
  };
}
